/**
 * FIX 4.4 Message Generator
 * Generates FIX messages for sending trades to:
 * - Tradeweb (government bonds)
 * - MarketAxess (corporate bonds)
 * - EMSX (equities via Bloomberg)
 */

export interface TradeData {
  id: number;
  cusip?: string;
  isin?: string;
  securityName?: string;
  transactionType: string;
  amount: number;
  price: number;
  tradeDate: string;
  settleDate: string;
  portfolio?: string;
  portfolioName?: string;
  assetClass?: string;
  assetClassName?: string;
  // NAV-based fields for privates/real estate
  navPerUnit?: number;
  units?: number;
  valuationMethod?: string;
}

export interface FixMessage {
  message: string;
  formatted: string;
}

/**
 * Generate FIX 4.4 New Order Single (D) message
 * Tag reference: https://www.onixs.biz/fix-dictionary/4.4/msgType_D_68.html
 * 
 * Supports both FIX execution (for venues that support it) and manual execution workflow
 * for OTC trades like privates and real estate
 */
export function generateFixMessage(trade: TradeData, venue: 'Tradeweb' | 'MarketAxess' | 'EMSX' | 'MANUAL'): FixMessage {
  // For manual execution, return a placeholder message indicating manual workflow
  if (venue === 'MANUAL') {
    return {
      message: 'MANUAL_EXECUTION_REQUIRED',
      formatted: `Manual Execution Required\nAsset Class: ${trade.assetClassName || 'Unknown'}\nTrade ID: ${trade.id}\nThis trade requires manual execution workflow (email confirmation, broker confirmation, etc.)`
    };
  }
  const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
  const msgSeqNum = Math.floor(Math.random() * 1000000);
  
  // FIX 4.4 Standard Header
  const beginString = '8=FIX.4.4';
  const bodyLength = '9=XXX'; // Will be calculated
  const msgType = '35=D'; // New Order Single
  const senderCompID = '49=OMS_CLIENT';
  const targetCompID = venue === 'Tradeweb' ? '56=TRADEWEB' : venue === 'MarketAxess' ? '56=MARKETAXESS' : '56=EMSX';
  const msgSeqNumTag = `34=${msgSeqNum}`;
  const sendingTime = `52=${timestamp}`;
  
  // Order Details
  const clOrdID = `11=${trade.id}_${Date.now()}`; // Unique client order ID
  const side = trade.transactionType === 'BUY' ? '54=1' : '54=2'; // 1=Buy, 2=Sell
  const transactTime = `60=${timestamp}`;
  const ordType = '40=2'; // Limit order
  const priceTag = `44=${trade.price.toFixed(4)}`;
  const orderQty = `38=${trade.amount}`;
  
  // Security Identification
  // Support both CUSIP and ISIN for privates/real estate
  const securityID = trade.cusip ? `48=${trade.cusip}` : (trade.isin ? `48=${trade.isin}` : '');
  const securityIDSource = trade.cusip ? '22=1' : (trade.isin ? '22=4' : ''); // 1=CUSIP, 4=ISIN
  const securityDesc = trade.securityName ? `107=${trade.securityName}` : '';
  
  // NAV-based pricing for privates/real estate (if venue supports it)
  const navPerUnit = trade.navPerUnit ? `1150=${trade.navPerUnit.toFixed(4)}` : ''; // Custom tag for NAV
  const units = trade.units ? `1151=${trade.units.toFixed(4)}` : ''; // Custom tag for units
  
  // Settlement
  const settlType = '63=0'; // Regular settlement
  const settlDate = `64=${trade.settleDate.replace(/-/g, '')}`;
  
  // Account
  const account = trade.portfolioName || trade.portfolio ? `1=${trade.portfolioName || trade.portfolio}` : '';
  
  // Time in Force
  const timeInForce = '59=0'; // Day order
  
  // Build message parts
  const messageParts = [
    beginString,
    bodyLength,
    msgType,
    senderCompID,
    targetCompID,
    msgSeqNumTag,
    sendingTime,
    clOrdID,
    side,
    transactTime,
    ordType,
    priceTag,
    orderQty,
    timeInForce,
    settlType,
    settlDate,
    securityID,
    securityIDSource,
    securityDesc,
    account,
    navPerUnit,
    units
  ].filter(part => part !== ''); // Remove empty parts
  
  // Calculate body length (everything after body length tag, before checksum)
  const bodyStart = messageParts.indexOf(bodyLength) + 1;
  const bodyContent = messageParts.slice(bodyStart).join('|');
  const bodyLengthValue = bodyContent.length;
  messageParts[messageParts.indexOf(bodyLength)] = `9=${bodyLengthValue}`;
  
  // Calculate checksum (sum of all ASCII values mod 256)
  const fullMessage = messageParts.join('|') + '|';
  let checksum = 0;
  for (let i = 0; i < fullMessage.length; i++) {
    checksum += fullMessage.charCodeAt(i);
  }
  checksum = checksum % 256;
  const checksumTag = `10=${checksum.toString().padStart(3, '0')}`;
  
  // Final message
  const fixMessage = [...messageParts, checksumTag].join('|');
  
  // Formatted version for display
  const formatted = messageParts.map(part => {
    const [tag, value] = part.split('=');
    const tagName = getTagName(parseInt(tag));
    return `${tag} (${tagName})=${value}`;
  }).join('\n') + `\n${checksumTag} (CheckSum)=${checksum.toString().padStart(3, '0')}`;
  
  return {
    message: fixMessage,
    formatted: formatted
  };
}

/**
 * Get FIX tag name for display purposes
 */
function getTagName(tag: number): string {
  const tagNames: Record<number, string> = {
    8: 'BeginString',
    9: 'BodyLength',
    11: 'ClOrdID',
    22: 'SecurityIDSource',
    34: 'MsgSeqNum',
    35: 'MsgType',
    38: 'OrderQty',
    40: 'OrdType',
    44: 'Price',
    48: 'SecurityID',
    49: 'SenderCompID',
    52: 'SendingTime',
    54: 'Side',
    56: 'TargetCompID',
    59: 'TimeInForce',
    60: 'TransactTime',
    63: 'SettlType',
    64: 'SettlDate',
    107: 'SecurityDesc',
    1: 'Account',
    10: 'CheckSum'
  };
  return tagNames[tag] || `Tag${tag}`;
}

/**
 * Determine if a bond is a government bond based on asset class
 */
export function isGovernmentBond(assetClass: string | undefined): boolean {
  if (!assetClass) return false;
  
  const govBondClasses = [
    'Government',
    'Treasury',
    'Agency',
    'Municipal',
    'Muni',
    'Sovereign'
  ];
  
  return govBondClasses.some(govClass => 
    assetClass.toLowerCase().includes(govClass.toLowerCase())
  );
}

